import { zodResolver } from '@hookform/resolvers/zod';
import CurrencyExchangeRounded from '@mui/icons-material/CurrencyExchangeRounded';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { api } from '../../api/client';
import { money } from '../../lib/format';
import { useNotify } from '../../lib/notify';
import { useShop } from '../../shop/ShopProvider';
import {
  idempotency,
  invalidateMoney,
  unwrapPayment,
  useOpenItems,
  usePaymentMethods,
} from './kit/hooks';
import { links, todayIn } from './kit/labels';
import { MethodSelect } from './kit/MethodSelect';
import { refundSchema, type RefundFormValues } from './schemas';

/**
 * Pays out a party's unallocated credit (customer refund) or records money a supplier sends back
 * (supplier refund). The API takes the credit oldest first.
 */
export function RefundDialog({
  open,
  onClose,
  contact,
  party,
}: {
  open: boolean;
  onClose: () => void;
  contact: { id: string; name: string };
  party: 'customer' | 'supplier';
}) {
  const { format } = useShop();
  const notify = useNotify();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const ledger = party === 'customer' ? 'RECEIVABLE' : 'PAYABLE';
  const openItems = useOpenItems(open ? contact.id : null, ledger);
  const methods = usePaymentMethods();
  const credit = openItems.data?.totalCredit ?? '0';
  const schema = useMemo(
    () => refundSchema(credit, format.currencyDecimals),
    [credit, format.currencyDecimals],
  );
  const cash = methods.data?.data.find((m) => m.kind === 'CASH' && m.isActive);

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<RefundFormValues>({
    resolver: zodResolver(schema),
    values: {
      amount: openItems.data ? credit : '',
      methodId: cash?.id ?? '',
      paymentDate: todayIn(format.timeZone),
      reference: '',
    },
    resetOptions: { keepDirtyValues: true },
  });

  const save = useMutation({
    mutationFn: (v: RefundFormValues) =>
      unwrapPayment(
        api.POST('/payments', {
          params: idempotency(),
          body: {
            purpose: party === 'customer' ? 'CUSTOMER_REFUND' : 'SUPPLIER_REFUND',
            contactId: contact.id,
            amount: v.amount,
            methodId: v.methodId,
            paymentDate: v.paymentDate,
            reference: v.reference || null,
          },
        }),
      ),
    onSuccess: async (p) => {
      notify.success(`Refund ${p.number} recorded`);
      await invalidateMoney(qc);
      reset();
      onClose();
      void navigate(links.payment(p.id));
    },
    onError: (e) => notify.error(e),
  });

  const noCredit = openItems.data && Number(credit) <= 0;

  return (
    <Dialog open={open} onClose={save.isPending ? undefined : onClose} maxWidth="xs" fullWidth>
      <form noValidate onSubmit={(e) => void handleSubmit((v) => save.mutate(v))(e)}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
            <CurrencyExchangeRounded color="primary" />
            <span>{party === 'customer' ? 'Refund customer credit' : 'Supplier refund'}</span>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              p: 2,
              mb: 2.5,
              borderRadius: '12px',
              border: 1,
              borderColor: 'divider',
              bgcolor: 'color-mix(in srgb, var(--mui-palette-primary-main) 4%, transparent)',
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Available credit · {contact.name}
            </Typography>
            <Typography variant="h4" className="tabular">
              {openItems.isLoading ? <Skeleton width={140} /> : money(credit, format)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {party === 'customer'
                ? 'Money you pay back to the customer.'
                : 'Money the supplier pays back to you.'}
            </Typography>
          </Box>
          {noCredit ? (
            <Alert severity="info">
              {contact.name} has no unallocated credit to refund right now.
            </Alert>
          ) : (
            <Stack spacing={2}>
              <TextField
                {...register('amount')}
                label="Amount"
                autoFocus
                error={Boolean(errors.amount)}
                helperText={errors.amount?.message}
                slotProps={{ htmlInput: { inputMode: 'decimal', className: 'tabular' } }}
              />
              <Controller
                control={control}
                name="methodId"
                render={({ field, fieldState }) => (
                  <MethodSelect
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
              <Stack direction="row" spacing={2}>
                <TextField
                  {...register('paymentDate')}
                  type="date"
                  label="Date"
                  fullWidth
                  error={Boolean(errors.paymentDate)}
                  helperText={errors.paymentDate?.message}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
                <TextField
                  {...register('reference')}
                  label="Reference"
                  fullWidth
                  error={Boolean(errors.reference)}
                  helperText={errors.reference?.message}
                />
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="outlined" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            loading={save.isPending}
            disabled={!openItems.data || Boolean(noCredit)}
          >
            Record refund
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
